import React, {useState, useCallback, useMemo, useEffect} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {toast} from 'react-toastify';
import {BiometricsService, type BiometricEntry} from '../services/biometricsService';
import Icon from '@/shared/components/Icon';
import Button from '@/shared/components/Button';

// Measurement keys + units mirror the biometrics store (see OHD syncService
// BIOMETRIC_LOINC_MAP). Patient self-entries post here as validated=false so
// they build their own trend history alongside the clinically-validated set.
const MEASUREMENT_UNITS: Record<string, string> = {
    height: 'inches',
    weight: 'pounds',
    waist: 'inches',
    bp_systolic: 'mmHg',
    bp_diastolic: 'mmHg',
};

// Measurements this form edits, minus height (handled separately as ft/in and
// hidden when a clinical reading is locked).
const NON_HEIGHT_MEASURES = ['weight', 'waist', 'bp_systolic', 'bp_diastolic'] as const;

interface HealthProfileData {
    heightInches?: number;
    weightLbs?: number;
    waistInches?: number;
    bpSystolic?: number;
    bpDiastolic?: number;
    medicalHistory?: string[];
    medicalConditions?: string[];
    allergies?: string[];
    profilePhotoUrl?: string | null;
}

// One self-reported reading day — the set of patient measurements recorded on
// a single date, each with its row id so the editor can PUT/DELETE in place.
interface EntryGroup {
    recorded_on: string; // YYYY-MM-DD
    fields: Record<string, {id: number; value: string}>;
}

// One write the save handler performs against the biometrics API.
type Op =
    | {kind: 'post'; measurement: string; value: string; recorded_on: string}
    | {kind: 'put'; id: number; value: string}
    | {kind: 'delete'; id: number};

interface Props {
    profile: HealthProfileData;
    /** All biometric history for the user, keyed by measurement. The editor
     *  derives the self-reported (patient, validated=false) entries from it. */
    history?: Record<string, BiometricEntry[]>;
    /**
     * True when a provider/OHD-validated height is on file. Patients can't
     * overwrite a clinically-recorded height through this UI, so the field
     * is hidden in that case. When false, the patient can self-report it.
     */
    heightLocked?: boolean;
    onClose: () => void;
}

const todayStr = () => new Date().toISOString().slice(0, 10);

function formatDateLabel(date: string): string {
    // Append a local midnight so the YYYY-MM-DD string doesn't shift a day
    // under the browser's timezone.
    const d = new Date(`${date}T00:00:00`);
    return Number.isNaN(d.getTime())
        ? date
        : d.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'});
}

const HealthProfileEditModal: React.FC<Props> = ({profile, history, heightLocked = false, onClose}) => {
    const queryClient = useQueryClient();

    // Self-reported entries, grouped by date, most-recent first. Index 0 of the
    // carousel is a blank "New Entry"; indexes 1..N edit groups[0..N-1].
    const groups = useMemo<EntryGroup[]>(() => {
        if (!history) return [];
        const byDate = new Map<string, EntryGroup>();
        for (const [measurement, entries] of Object.entries(history)) {
            for (const e of entries) {
                if (e.validated || e.source !== 'patient') continue;
                const date = e.recorded_on.slice(0, 10);
                let g = byDate.get(date);
                if (!g) {
                    g = {recorded_on: date, fields: {}};
                    byDate.set(date, g);
                }
                g.fields[measurement] = {id: e.id, value: String(e.value)};
            }
        }
        return [...byDate.values()].sort((a, b) => b.recorded_on.localeCompare(a.recorded_on));
    }, [history]);

    // Carousel position. 0 = New Entry; i>=1 edits groups[i-1].
    const [index, setIndex] = useState(0);
    const isNew = index === 0;
    const currentGroup = isNew ? null : groups[index - 1];

    const [heightFt, setHeightFt] = useState<string | number>('');
    const [heightIn, setHeightIn] = useState<string | number>('');
    const [weight, setWeight] = useState<string | number>('');
    const [waist, setWaist] = useState<string | number>('');
    const [bpSystolic, setBpSystolic] = useState<string | number>('');
    const [bpDiastolic, setBpDiastolic] = useState<string | number>('');
    const [recordedOn, setRecordedOn] = useState(todayStr);

    // Populate the fields whenever the visible entry changes (navigation) —
    // blank for New Entry, the stored values for an existing day.
    useEffect(() => {
        if (isNew) {
            setHeightFt('');
            setHeightIn('');
            setWeight('');
            setWaist('');
            setBpSystolic('');
            setBpDiastolic('');
            setRecordedOn(todayStr());
            return;
        }
        const g = groups[index - 1];
        if (!g) return;
        const h = g.fields.height ? Number(g.fields.height.value) : 0;
        setHeightFt(h ? Math.floor(h / 12) : '');
        setHeightIn(h ? h % 12 : '');
        setWeight(g.fields.weight?.value ?? '');
        setWaist(g.fields.waist?.value ?? '');
        setBpSystolic(g.fields.bp_systolic?.value ?? '');
        setBpDiastolic(g.fields.bp_diastolic?.value ?? '');
        setRecordedOn(g.recorded_on);
        // groups is stable while the modal is open; we intentionally re-run only
        // on navigation so background refetches don't clobber in-progress edits.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [index]);

    const mutation = useMutation({
        mutationFn: async (ops: Op[]) => {
            // Sequential keeps ordering deterministic and avoids hammering the
            // API; the op count per save is tiny (<=5).
            for (const op of ops) {
                if (op.kind === 'post') {
                    await BiometricsService.add({
                        measurement: op.measurement,
                        value: op.value,
                        units: MEASUREMENT_UNITS[op.measurement],
                        recorded_on: op.recorded_on,
                    });
                } else if (op.kind === 'put') {
                    await BiometricsService.update(op.id, {value: op.value});
                } else {
                    await BiometricsService.remove(op.id);
                }
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['biometrics-latest']});
            queryClient.invalidateQueries({queryKey: ['biometrics-history']});
            queryClient.invalidateQueries({queryKey: ['health-profile']});
            toast.success(isNew ? 'Measurements saved' : 'Entry updated');
            onClose();
        },
        onError: (err) => {
            toast.error(err instanceof Error && err.message ? err.message : 'Failed to save measurements');
        },
    });

    // Collect the current form values keyed by measurement (only the ones the
    // patient actually filled in).
    const collectFieldValues = useCallback((): Record<string, string> => {
        const out: Record<string, string> = {};
        if (!heightLocked) {
            const totalIn = Number(heightFt) * 12 + Number(heightIn);
            if (totalIn > 0) out.height = String(totalIn);
        }
        if (weight !== '' && Number(weight) > 0) out.weight = String(weight);
        if (waist !== '' && Number(waist) > 0) out.waist = String(waist);
        if (bpSystolic !== '' && Number(bpSystolic) > 0) out.bp_systolic = String(bpSystolic);
        if (bpDiastolic !== '' && Number(bpDiastolic) > 0) out.bp_diastolic = String(bpDiastolic);
        return out;
    }, [heightLocked, heightFt, heightIn, weight, waist, bpSystolic, bpDiastolic]);

    const handleSubmit = useCallback(
        (e: React.FormEvent) => {
            e.preventDefault();
            const values = collectFieldValues();
            const ops: Op[] = [];

            if (isNew) {
                for (const [measurement, value] of Object.entries(values)) {
                    ops.push({kind: 'post', measurement, value, recorded_on: recordedOn});
                }
                if (ops.length === 0) {
                    toast.info('Enter at least one measurement');
                    return;
                }
            } else if (currentGroup) {
                // Diff each measurement against what's stored for this day.
                const measures = heightLocked ? NON_HEIGHT_MEASURES : (['height', ...NON_HEIGHT_MEASURES] as const);
                for (const m of measures) {
                    const existing = currentGroup.fields[m];
                    const next = values[m];
                    if (existing && next != null && next !== existing.value) {
                        ops.push({kind: 'put', id: existing.id, value: next});
                    } else if (existing && next == null) {
                        ops.push({kind: 'delete', id: existing.id});
                    } else if (!existing && next != null) {
                        ops.push({kind: 'post', measurement: m, value: next, recorded_on: currentGroup.recorded_on});
                    }
                }
                if (ops.length === 0) {
                    toast.info('No changes to save');
                    return;
                }
            }
            mutation.mutate(ops);
        },
        [isNew, currentGroup, heightLocked, recordedOn, collectFieldValues, mutation]
    );

    // Delete every reading on the current day.
    const handleDeleteEntry = useCallback(() => {
        if (!currentGroup) return;
        const ops: Op[] = Object.values(currentGroup.fields).map((f) => ({kind: 'delete', id: f.id}));
        if (ops.length === 0) return;
        // Move the carousel toward New Entry so we don't land on a now-gone day.
        setIndex(0);
        mutation.mutate(ops);
    }, [currentGroup, mutation]);

    const canOlder = index < groups.length; // older entry exists
    const canNewer = index > 0; // a newer position exists (toward New Entry)

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={onClose} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                {/* Header — carousel nav (older ← title → newer) + close. */}
                <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-3 flex items-center justify-between rounded-t-xl">
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={() => setIndex((i) => i + 1)}
                            disabled={!canOlder}
                            title="Previous (older) entry"
                            aria-label="Previous entry"
                            className="p-1 rounded-lg text-gray-500 enabled:hover:bg-gray-100 disabled:opacity-30 transition-colors">
                            <Icon name="chevron-left" className="w-5 h-5" />
                        </button>
                        <div className="min-w-[9.5rem] text-center">
                            <h2 className="text-base font-semibold text-mld-text-primary leading-tight">
                                {isNew ? 'New Entry' : formatDateLabel(currentGroup!.recorded_on)}
                            </h2>
                            <p className="text-[11px] text-gray-400 leading-tight">
                                {isNew
                                    ? groups.length > 0
                                        ? '← edit past entries'
                                        : 'Add a reading'
                                    : `Editing · ${index} of ${groups.length}`}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setIndex((i) => i - 1)}
                            disabled={!canNewer}
                            title="Next (newer) entry"
                            aria-label="Next entry"
                            className="p-1 rounded-lg text-gray-500 enabled:hover:bg-gray-100 disabled:opacity-30 transition-colors">
                            <Icon name="chevron-right" className="w-5 h-5" />
                        </button>
                    </div>
                    <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 transition-colors">
                        <Icon name="xmark" className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="px-5 py-4 space-y-5">
                    {/* Read-only medical history + allergies. Sourced from
                        intake / provider entry, not editable here. Hidden when
                        both are empty so the modal stays tight. Only shown on the
                        New Entry view to keep edit views focused. */}
                    {isNew &&
                        (() => {
                            const conditions = profile.medicalConditions ?? profile.medicalHistory ?? [];
                            const allergies = profile.allergies ?? [];
                            if (conditions.length === 0 && allergies.length === 0) return null;
                            return (
                                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3 text-sm">
                                    {conditions.length > 0 && (
                                        <div>
                                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                                                Medical History
                                            </div>
                                            <p className="text-mld-text-primary">{conditions.join(', ')}</p>
                                        </div>
                                    )}
                                    {allergies.length > 0 && (
                                        <div>
                                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                                                Allergies
                                            </div>
                                            <p className="text-mld-text-primary">{allergies.join(', ')}</p>
                                        </div>
                                    )}
                                    <p className="text-[11px] text-gray-500">
                                        Recorded by your provider. Contact support@mylabsdirect.com to update.
                                    </p>
                                </div>
                            );
                        })()}

                    {/* Date — editable when adding a new reading; read-only when
                        editing an existing day (it identifies the entry). */}
                    {isNew ? (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Date measured</label>
                            <input
                                type="date"
                                value={recordedOn}
                                max={todayStr()}
                                onChange={(e) => setRecordedOn(e.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                            />
                        </div>
                    ) : (
                        <div className="text-sm text-gray-500">
                            Measured <span className="font-medium text-mld-text-primary">{formatDateLabel(recordedOn)}</span>
                        </div>
                    )}

                    {/* Height — hidden when a provider/OHD reading already locked
                        the value. Patients self-report only when no clinical
                        record exists. */}
                    {!heightLocked && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Height</label>
                            <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1">
                                    <input
                                        type="number"
                                        min={0}
                                        max={8}
                                        value={heightFt}
                                        onChange={(e) => setHeightFt(e.target.value === '' ? '' : Number(e.target.value))}
                                        className="w-16 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                        placeholder="ft"
                                    />
                                    <span className="text-sm text-gray-500">ft</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <input
                                        type="number"
                                        min={0}
                                        max={11}
                                        value={heightIn}
                                        onChange={(e) => setHeightIn(e.target.value === '' ? '' : Number(e.target.value))}
                                        className="w-16 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                        placeholder="in"
                                    />
                                    <span className="text-sm text-gray-500">in</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Weight */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Weight (lbs)</label>
                        <input
                            type="number"
                            min={30}
                            max={1000}
                            step={0.1}
                            value={weight}
                            onChange={(e) => setWeight(e.target.value === '' ? '' : Number(e.target.value))}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                            placeholder="e.g. 185"
                        />
                    </div>

                    {/* Waist */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Waist (inches)</label>
                        <input
                            type="number"
                            min={10}
                            max={100}
                            step={0.1}
                            value={waist}
                            onChange={(e) => setWaist(e.target.value === '' ? '' : Number(e.target.value))}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                            placeholder="e.g. 32"
                        />
                    </div>

                    {/* Blood Pressure */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Blood Pressure</label>
                        <div className="flex items-center gap-2">
                            <input
                                type="number"
                                min={60}
                                max={250}
                                value={bpSystolic}
                                onChange={(e) => setBpSystolic(e.target.value === '' ? '' : Number(e.target.value))}
                                className="w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                placeholder="Systolic"
                            />
                            <span className="text-gray-400">/</span>
                            <input
                                type="number"
                                min={30}
                                max={200}
                                value={bpDiastolic}
                                onChange={(e) => setBpDiastolic(e.target.value === '' ? '' : Number(e.target.value))}
                                className="w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                placeholder="Diastolic"
                            />
                        </div>
                        {!isNew && (
                            <p className="mt-1 text-[11px] text-gray-400">Clear a field and save to remove that reading.</p>
                        )}
                    </div>

                    {/* Footer — delete (edit only) on the left, cancel/save right. */}
                    <div className="pt-2 flex items-center justify-between gap-3">
                        {!isNew ? (
                            <button
                                type="button"
                                onClick={handleDeleteEntry}
                                disabled={mutation.isPending}
                                className="inline-flex items-center gap-1 text-sm font-medium text-mld-status-high hover:underline disabled:opacity-50">
                                <Icon name="trash-can" className="w-4 h-4" />
                                Delete entry
                            </button>
                        ) : (
                            <span />
                        )}
                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                                Cancel
                            </button>
                            <Button type="submit" loading={mutation.isPending}>
                                {mutation.isPending ? 'Saving...' : isNew ? 'Save' : 'Update'}
                            </Button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default HealthProfileEditModal;
